<template>
  <div class="kdo-overlay" @mousedown.self="close">
    <div class="kdo-dialog">
      <h3>{{ title }}</h3>
      <div class="kdo-hint">{{ subtitle }}</div>
      <a class="kdo-help-link" :href="helpHref('params')" target="_blank" rel="noopener">
        📖 Что означают параметры поля — справочник с примерами
      </a>

      <div class="kdo-form">
        <div class="row">
          <div>
            <label class="caption">Ключ (макрос в документе)</label>
            <input v-model="form.key" type="text" :disabled="!isNew" placeholder="например workAddress">
            <div class="note">
              <template v-if="isNew">
                Латиница, цифры, «_». В Word пишется как
                <span class="kdo-macro">${{ '{' + (form.key || 'ключ') + '}' }}</span>
              </template>
              <template v-else>Ключ нельзя менять — по нему значения хранятся в документах</template>
            </div>
          </div>
          <div>
            <label class="caption">Название на форме</label>
            <input v-model="form.label" type="text" placeholder="Адрес места работы">
          </div>
        </div>

        <div class="row">
          <div>
            <label class="caption">Вид поля на форме</label>
            <select v-model="form.workAs" :disabled="locked" @change="onWorkAsChange">
              <option v-for="item in dict.workAs" :key="item.key" :value="item.key">{{ item.title }}</option>
            </select>
          </div>
          <div>
            <label class="caption">Тип значения</label>
            <select v-model="form.valueType" :disabled="locked || allowedValueTypes.length < 2">
              <option v-for="type in allowedValueTypes" :key="type.key" :value="type.key">{{ type.title }}</option>
            </select>
          </div>
        </div>

        <div v-if="locked" class="note">
          Системное поле: его использует интеграция (Енбек и др.), поэтому вид, тип и источник менять нельзя —
          можно поменять название и дополнительные настройки.
        </div>

        <!-- дата -->
        <div v-if="form.valueType === 'date'" class="row one">
          <div>
            <label class="caption">Формат даты в документе</label>
            <input v-model="form.dateFormat" type="text" placeholder="d.m.Y">
            <div class="chips">
              <span v-for="preset in datePresets" :key="preset" class="chip" @click="form.dateFormat = preset">{{ preset }}</span>
            </div>
            <div class="note">d — день, m — месяц, Y — год. Пример: «d.m.Y» → 05.10.2026</div>
          </div>
        </div>

        <!-- проверка строки -->
        <div v-if="form.valueType === 'string' && form.workAs !== 'select'" class="row">
          <div>
            <label class="caption">Значение должно содержать символ (необязательно)</label>
            <input v-model="form.rules.str_must_contain" type="text" placeholder=":">
          </div>
          <div>
            <label class="caption">Точная длина значения (необязательно)</label>
            <input v-model="form.rules.length" type="number" min="1" max="1000" placeholder="5">
          </div>
        </div>

        <!-- выбор -->
        <template v-if="form.workAs === 'select'">
          <div class="row">
            <div>
              <label class="caption">Источник вариантов</label>
              <select v-model="form.dataSource" :disabled="locked" @change="onSourceChange">
                <option v-for="source in dict.dataSources" :key="source.key" :value="source.key">{{ source.title }}</option>
              </select>
            </div>
            <div v-if="neededFieldOptions.length">
              <label class="caption">Что сохранять в документ из записи</label>
              <select v-model="form.neededField">
                <option value="">— сама запись (id) —</option>
                <option v-for="attr in neededFieldOptions" :key="attr" :value="attr">{{ attr }}</option>
              </select>
              <div class="note">Например, «title» — в документе будет название отдела/должности</div>
            </div>
          </div>

          <div v-if="form.dataSource === 'static'" class="items">
            <div class="section-title">Варианты выбора</div>
            <div v-for="(option, index) in form.options" :key="index" class="item">
              <input v-model="option.value" type="text" placeholder="значение">
              <input v-model="option.title" type="text" placeholder="название">
              <button type="button" class="icon-btn danger" @click="form.options.splice(index, 1)">✕</button>
            </div>
            <button type="button" class="kdo-btn small" @click="form.options.push({ value: '', title: '', })">+ Добавить вариант</button>
          </div>
        </template>

        <!-- список -->
        <div v-if="form.workAs === 'list'" class="items">
          <div class="section-title">Пункты по умолчанию</div>
          <div class="note" style="margin-bottom: 8px">
            Пользователь увидит эти пункты при создании документа и сможет менять, добавлять, удалять и переставлять их.
            В документе список печатается нумерованными абзацами.
          </div>
          <div v-for="(item, index) in form.defaultValue" :key="index" class="item">
            <input v-model="form.defaultValue[index]" type="text">
            <button type="button" class="icon-btn" :disabled="index === 0" @click="move(index, -1)">↑</button>
            <button type="button" class="icon-btn" :disabled="index === form.defaultValue.length - 1" @click="move(index, 1)">↓</button>
            <button type="button" class="icon-btn danger" @click="form.defaultValue.splice(index, 1)">✕</button>
          </div>
          <button type="button" class="kdo-btn small" @click="form.defaultValue.push('')">+ Добавить пункт</button>
        </div>

        <!-- дополнительно -->
        <div class="section-title">
          <span style="cursor: pointer" @click="showAdvanced = !showAdvanced">{{ showAdvanced ? '▾' : '▸' }} Дополнительно</span>
        </div>
        <div v-if="showAdvanced">
          <div class="row">
            <div v-if="form.workAs === 'select'">
              <label class="caption">Подпись вариантов (поле записи)</label>
              <input v-model="form.selectLabel" type="text" placeholder="applicationTitle">
            </div>
            <div v-if="form.workAs === 'select' && form.dataSource && modelSource">
              <label class="caption">Положить запись в документ как</label>
              <input v-model="form.moveModelTo" type="text" placeholder="headOfHRModel">
              <div class="note">Имя должно заканчиваться на «Model»</div>
            </div>
          </div>
          <div class="row">
            <div>
              <label class="caption">Подставлять значение из другого поля</label>
              <input v-model="form.couldBeFrom" type="text" placeholder="positionTo.enbekPositionCode">
              <div class="note">ключ_поля.атрибут, например application.authorId</div>
            </div>
            <div>
              <label class="caption">Блокировать поле, пока заполнено</label>
              <select v-model="form.disabledWhen">
                <option value="">— не блокировать —</option>
                <option v-for="other in otherFields" :key="other.key" :value="other.key">{{ other.label }} ({{ other.key }})</option>
              </select>
            </div>
          </div>
          <div class="row">
            <div>
              <label class="caption">Порядок показа</label>
              <input v-model.number="form.sortOrder" type="number">
            </div>
            <div style="display: flex; align-items: flex-end">
              <label class="check"><input v-model="form.isActive" type="checkbox"> Поле активно</label>
            </div>
          </div>
        </div>

        <!-- предпросмотр -->
        <div class="section-title">Как это увидит пользователь</div>
        <div class="preview">
          <label class="caption">{{ form.label || 'Название поля' }}</label>
          <textarea v-if="form.workAs === 'textarea'" rows="3" disabled placeholder="Многострочный текст"></textarea>
          <select v-else-if="form.workAs === 'select'" disabled><option>Выберите значение</option></select>
          <input v-else-if="form.workAs === 'date-input'" type="text" disabled placeholder="дд.мм.гггг">
          <input v-else-if="form.workAs === 'hours-input'" type="text" disabled placeholder="ЧЧ:ММ">
          <div v-else-if="form.workAs === 'list'" class="note">
            <div v-for="(item, index) in form.defaultValue.filter((v) => v)" :key="index">{{ index + 1 }}. {{ item }}</div>
            <div v-if="!form.defaultValue.filter((v) => v).length">Пустой список — пользователь добавит пункты сам</div>
          </div>
          <input v-else type="text" disabled placeholder="Текст">
        </div>

        <div v-if="error" class="error">{{ error }}</div>
      </div>

      <div class="kdo-actions">
        <button type="button" class="kdo-btn" :disabled="saving" @click="close">Отмена</button>
        <button type="button" class="kdo-btn primary" :disabled="saving" @click="save">{{ saving ? 'Сохраняем...' : 'Сохранить' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CKdoFieldEditor',
  props: {
    // null — глобальная библиотека
    companyId: { type: [ Number, String, ], default: null, },
    // существующее поле (строка списка) или null для нового
    field: { type: Object, default: null, },
    dict: { type: Object, required: true, },
    allFields: { type: Array, default: () => [], },
    presetKey: { type: String, default: '', },
  },
  data() {
    return {
      form: this.initialForm(),
      saving: false,
      error: '',
      showAdvanced: false,
      datePresets: [ 'd.m.Y', 'd m Y', '«d» m Y года', '«d» m Y жылғы', 'Y-m-d', ],
    }
  },
  computed: {
    // поле библиотеки, открытое в режиме компании: сохранение создаёт свою версию
    isOverrideCreate() {
      return !!this.companyId && !!this.field && this.field.origin === 'global'
    },
    isNew() {
      return !this.field || this.isOverrideCreate
    },
    locked() {
      return !!this.field && this.field.isSystem
    },
    title() {
      if (this.isOverrideCreate) return 'Своя версия поля для компании'
      return this.field ? 'Изменить поле' : 'Новое поле'
    },
    subtitle() {
      if (this.isOverrideCreate) return 'Это поле из общей библиотеки. После сохранения у компании появится своя версия, библиотека не изменится.'
      return this.companyId ? 'Поле будет доступно только этой компании.' : 'Поле попадёт в общую библиотеку и станет доступно всем компаниям.'
    },
    allowedValueTypes() {
      const workAs = $n.find(this.dict.workAs, (item) => item.key === this.form.workAs)
      const allowed = workAs ? workAs.valueTypes : []
      return this.dict.valueTypes.filter((type) => allowed.includes(type.key))
    },
    modelSource() {
      const source = $n.find(this.dict.dataSources, (item) => item.key === this.form.dataSource)
      return !!(source && source.isModel)
    },
    neededFieldOptions() {
      return (this.dict.neededFieldsBySource && this.dict.neededFieldsBySource[this.form.dataSource]) || []
    },
    otherFields() {
      return this.allFields.filter((item) => item.key !== this.form.key)
    },
  },
  methods: {
    initialForm() {
      const field = this.field
      return {
        key: field ? field.key : this.presetKey,
        label: field ? field.label : '',
        workAs: field ? field.workAs : 'input',
        valueType: field ? field.valueType : 'string',
        dateFormat: field && field.dateFormat ? field.dateFormat : '',
        rules: {
          str_must_contain: field && field.rules && field.rules.str_must_contain ? field.rules.str_must_contain : '',
          length: field && field.rules && field.rules.length ? field.rules.length : '',
        },
        dataSource: field ? field.dataSource : null,
        options: field ? JSON.parse(JSON.stringify(field.options || [])) : [],
        selectLabel: field && field.selectLabel ? field.selectLabel : '',
        neededField: field && field.neededField ? field.neededField : '',
        moveModelTo: field && field.moveModelTo ? field.moveModelTo : '',
        couldBeFrom: field && field.couldBeFrom ? field.couldBeFrom : '',
        disabledWhen: field && field.disabledWhen ? field.disabledWhen : '',
        defaultValue: field ? (field.defaultValue || []).slice() : [],
        isActive: field ? field.isActive : true,
        sortOrder: field ? field.sortOrder : 1000,
      }
    },
    onWorkAsChange() {
      if (!this.allowedValueTypes.some((type) => type.key === this.form.valueType)) {
        this.form.valueType = this.allowedValueTypes[0].key
      }
      if (this.form.workAs === 'select' && !this.form.dataSource) {
        this.form.dataSource = this.dict.dataSources[0].key
        this.onSourceChange()
      }
      if (this.form.workAs !== 'select') {
        this.form.dataSource = null
      }
      if (this.form.valueType === 'date' && !this.form.dateFormat) {
        this.form.dateFormat = 'd.m.Y'
      }
    },
    onSourceChange() {
      const source = $n.find(this.dict.dataSources, (item) => item.key === this.form.dataSource)
      if (source && source.isModel) {
        this.form.valueType = 'integer'
      } else if (this.form.dataSource === 'positionsEnbek') {
        this.form.valueType = 'string'
      }
      this.form.neededField = ''
    },
    move(index, shift) {
      const items = this.form.defaultValue
      const target = index + shift
      if (target < 0 || target >= items.length) return
      const [ moved, ] = items.splice(index, 1)
      items.splice(target, 0, moved)
    },
    // справочник открывается в новой вкладке, чтобы не потерять заполненную форму
    helpHref(section) {
      return this.$router.resolve({ name: 'kdo-help', hash: '#' + section, }).href
    },
    close() {
      if (this.saving) return
      this.$emit('close')
    },
    save() {
      this.error = ''
      const payload = {
        key: this.form.key,
        label: this.form.label,
        workAs: this.form.workAs,
        valueType: this.form.valueType,
        dateFormat: this.form.valueType === 'date' ? this.form.dateFormat : null,
        rules: this.form.rules,
        dataSource: this.form.workAs === 'select' ? this.form.dataSource : null,
        options: this.form.options,
        selectLabel: this.form.selectLabel,
        neededField: this.form.neededField,
        moveModelTo: this.form.moveModelTo,
        couldBeFrom: this.form.couldBeFrom,
        disabledWhen: this.form.disabledWhen,
        defaultValue: this.form.defaultValue,
        isActive: this.form.isActive,
        sortOrder: this.form.sortOrder,
      }

      this.saving = true
      const request = this.isNew ?
        $api.kdo.createField(this.companyId, payload) :
        $api.kdo.editField(this.field.id, payload)
      request.then(() => {
        this.$emit('saved')
      }).catch((err) => {
        this.error = err?.response?.data?.message || 'Не удалось сохранить поле'
      }).finally(() => {
        this.saving = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './kdo.scss';

.preview {
  background: #faf8ff;
  border: 1px dashed #ddd5f0;
  border-radius: 12px;
  padding: 14px;
}
</style>
